import {
  OrgChartImportResponse,
  OrgChartImportStatuses,
} from '@interfaces/economic-groups/responses/org-chart-import.response';

/** Etapas do cartão de progresso da importação de organograma (card 3578), na ordem do backend. */
export const ORG_CHART_IMPORT_STEPS = [
  'Arquivo enviado',
  'Lendo o organograma',
  'Conferindo CNPJs no cadastro',
  'Consultando a Receita',
] as const;

export type OrgChartImportStepState = 'done' | 'current' | 'pending';

export interface OrgChartImportStep {
  label: string;
  state: OrgChartImportStepState;
}

/** Situações em que a importação ainda está rodando no servidor. */
export function isOrgChartImportRunning(item: OrgChartImportResponse | null): boolean {
  return (
    item?.status === OrgChartImportStatuses.Reading ||
    item?.status === OrgChartImportStatuses.Reconciling
  );
}

/** Situações em que o cartão aparece no builder (lendo, pronta para revisar ou falhou). */
export function isOrgChartImportOpen(item: OrgChartImportResponse | null): boolean {
  return (
    isOrgChartImportRunning(item) ||
    item?.status === OrgChartImportStatuses.Ready ||
    item?.status === OrgChartImportStatuses.Failed
  );
}

/**
 * Índice da etapa em andamento (0 a 3), ou 4 quando todas terminaram. A frase `phase` do backend manda quando bate com
 * uma etapa conhecida; senão vale a situação (`Reading` = lendo, `Reconciling` = conferindo CNPJs). "Enviando para
 * leitura" conta como "Lendo o organograma": o arquivo já subiu.
 */
export function resolveOrgChartImportStepIndex(item: OrgChartImportResponse | null): number {
  if (!item) return 0;
  if (item.status === OrgChartImportStatuses.Ready) return ORG_CHART_IMPORT_STEPS.length;

  const phase = item.phase?.trim() ?? '';
  const byPhase = ORG_CHART_IMPORT_STEPS.findIndex(step => step === phase);
  if (byPhase > 0) return byPhase;

  return item.status === OrgChartImportStatuses.Reconciling ? 2 : 1;
}

/** Lista de etapas com a situação de cada uma, para o template. */
export function buildOrgChartImportSteps(
  item: OrgChartImportResponse | null
): OrgChartImportStep[] {
  const currentIndex = resolveOrgChartImportStepIndex(item);
  return ORG_CHART_IMPORT_STEPS.map((label, index) => ({
    label,
    state: index < currentIndex ? 'done' : index === currentIndex ? 'current' : 'pending',
  }));
}
